import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Input, Text } from '@teals/ui-web';
import { useAuth } from '../../hooks';
import { useLinkGoogleMutation } from '../../store/api/portalApi';
import { refreshSession, setSsoFlow } from '../../services/cognito';
import { setCredentials } from '../../store/slices';
import { useAppDispatch } from '../../store';
import { normalizeAuthEmail } from '../../utils/ssoLinkEmail';

const Container = styled.div`
  width: 100%;
  max-width: 380px;
`;

const Lead = styled.p`
  margin: 0 0 1.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ErrorMsg = styled.div`
  padding: 0.75rem 1rem;
  background: var(--error-subtle);
  color: var(--error);
  border-radius: 8px;
  font-size: 0.875rem;
`;

const Secondary = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;
  padding: 0;

  &:hover {
    color: var(--accent-hover);
  }
`;

const LinkAccounts: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { login, isLoading, error, user } = useAuth();
  const [linkGoogle, { isLoading: isLinking }] = useLinkGoogleMutation();
  const emailFromQuery = normalizeAuthEmail(
    new URLSearchParams(location.search).get('email') || ''
  );
  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const signedIn = await login(normalizeAuthEmail(email) || email, password);
    if (signedIn === false) {
      return;
    }
    try {
      await linkGoogle().unwrap();
      setSsoFlow('linked');
      const refreshed = await refreshSession();
      if (refreshed) {
        dispatch(
          setCredentials({
            user: {
              id: refreshed.user.userId,
              email: refreshed.user.email,
              name: refreshed.user.name,
              organizationId: refreshed.user.organizationId,
              groups: refreshed.user.groups,
            },
            token: refreshed.tokens.idToken,
            accessToken: refreshed.tokens.accessToken,
            refreshToken: refreshed.tokens.refreshToken,
          })
        );
        if (refreshed.user.organizationId) {
          navigate('/agents', { replace: true });
        } else {
          navigate('/agents', { replace: true });
        }
      } else if (user) {
        navigate('/agents', { replace: true });
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String(
              (err as { data?: { message?: string } }).data?.message ||
                'Could not link Google to this account.'
            )
          : err instanceof Error
            ? err.message
            : 'Could not link Google to this account.';
      setFormError(message);
    }
  };

  return (
    <Container>
      <Text variant="h3" style={{ marginBottom: '0.5rem' }}>
        Link your accounts
      </Text>
      <Lead>
        An account with this email already exists. Enter your Luminafactory password
        to link Google. You can keep signing in with either method.
      </Lead>
      {(formError || error) && <ErrorMsg>{formError || error}</ErrorMsg>}
      <Form onSubmit={handleSubmit}>
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          readOnly={Boolean(emailFromQuery)}
          fullWidth
          required
        />
        <Input
          label="Luminafactory password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your Luminafactory password"
          autoComplete="current-password"
          fullWidth
          required
        />
        <Button type="submit" fullWidth isLoading={isLoading || isLinking}>
          Link accounts
        </Button>
        <Secondary
          type="button"
          onClick={() => {
            setSsoFlow('');
            navigate('/auth');
          }}
        >
          Use a different email
        </Secondary>
      </Form>
    </Container>
  );
};

export default LinkAccounts;
